import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { EnvelopeIcon, LockClosedIcon, EyeIcon, EyeSlashIcon } from '@heroicons/react/24/outline';
import { API_URLS } from '../config/api';

function Login() {
  const [formData, setFormData] = useState({
    email: '',
    password: ''
  });
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [apiUrl, setApiUrl] = useState('');
  const navigate = useNavigate();
  const { login } = useAuth();
  const [showPassword, setShowPassword] = useState(false);

  // Determine the API URL on component mount
  useEffect(() => {
    // Get the current hostname
    const hostname = window.location.hostname;
    let baseUrl;
    
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
      baseUrl = API_URLS.default;
    } else {
      // For production, use the same hostname but with the backend port
      baseUrl = API_URLS.default;
    }
    
    console.log('Using API URL:', baseUrl);
    setApiUrl(baseUrl);
    
    // Check if the API is available
    fetch(`${baseUrl}/health`, {
      method: 'GET',
      mode: 'cors',
    })
    .then(response => {
      console.log('API health check successful:', response.status);
    })
    .catch(err => {
      console.error('API health check failed:', err);
    });
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prevState => ({
      ...prevState,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    if (!apiUrl) {
      setError('API URL not configured. Please try again.');
      setLoading(false);
      return;
    }

    try {
      console.log('Attempting login with:', {
        email: formData.email,
        url: `${apiUrl}/api/auth/login`
      });

      // Use XMLHttpRequest for better mobile compatibility
      const xhr = new XMLHttpRequest();
      xhr.open('POST', `${apiUrl}/api/auth/login`, true);
      xhr.setRequestHeader('Content-Type', 'application/json');
      xhr.timeout = 15000; // 15 seconds timeout
      
      xhr.onload = function() {
        if (xhr.status >= 200 && xhr.status < 300) {
          try {
            const data = JSON.parse(xhr.responseText);
            
            // Store token and user data
            login(data.user, data.token);
            
            // Redirect based on user type
            if (data.user.userType === 'rider') {
              navigate('/rider/dashboard');
            } else if (data.user.userType === 'driver') {
              navigate('/driver/dashboard');
            } else if (data.user.userType === 'admin') {
              navigate('/admin/dashboard');
            } else {
              navigate('/');
            }
          } catch (parseError) {
            console.error('Error parsing response:', parseError);
            setError('Invalid response from server');
            setLoading(false);
          }
        } else {
          try {
            const errorData = JSON.parse(xhr.responseText);
            
            // Check if this is a pending approval response
            if (errorData.requiresApproval && errorData.userType === 'driver') {
              // Redirect to pending approval page
              navigate('/pending-approval');
              return;
            }
            
            setError(errorData.message || 'Login failed');
          } catch (parseError) {
            setError(`Server error: ${xhr.status}`);
          }
          setLoading(false);
        }
      };
      
      xhr.onerror = function() {
        console.error('XHR error:', xhr.statusText);
        setError('Network error. Please check your connection and try again.');
        setLoading(false);
      };
      
      xhr.ontimeout = function() {
        setError('Request timed out. Please try again.');
        setLoading(false);
      };
      
      xhr.send(JSON.stringify({
        email: formData.email,
        password: formData.password
      }));
    } catch (err) {
      console.error('Login error:', err);
      setError(err.message || 'An unexpected error occurred');
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-indigo-500 via-purple-500 to-pink-500">
      <div className="container mx-auto px-4 h-full">
        <div className="flex content-center items-center justify-center min-h-screen py-12">
          <div className="w-full lg:w-5/12 px-4">
            <div className="relative flex flex-col min-w-0 break-words w-full mb-6 shadow-lg rounded-2xl bg-white border-0">
              <div className="rounded-t-2xl mb-0 px-8 pt-8">
                <div className="text-center mb-3">
                  <h1 className="text-3xl font-bold text-gray-900 mb-2">
                    Welcome Back
                  </h1>
                  <p className="text-gray-600 text-sm mb-4">
                    Sign in to your Chariots of Fire account
                  </p>
                </div>
              </div>

              <div className="flex-auto px-8 pb-8 pt-0">
                {error && (
                  <div className="mb-4 bg-red-50 border border-red-200 text-red-600 px-4 py-3 rounded-xl text-center">
                    {error}
                  </div>
                )}

                <form id="login-form" className="space-y-5" onSubmit={handleSubmit}>
                  <div className="relative">
                    <label htmlFor="email" className="block text-sm font-medium text-gray-700 mb-1">
                      Email address
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <EnvelopeIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="email"
                        name="email"
                        type="email"
                        inputMode="email"
                        autoComplete="email"
                        required
                        className="appearance-none block w-full pl-10 pr-3 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.email}
                        onChange={handleChange}
                        placeholder="john.doe@example.com"
                      />
                    </div>
                  </div>

                  <div className="relative">
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                      Password
                    </label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <LockClosedIcon className="h-5 w-5 text-indigo-500" />
                      </div>
                      <input
                        id="password"
                        name="password"
                        type={showPassword ? "text" : "password"}
                        autoComplete="current-password"
                        required
                        className="appearance-none block w-full pl-10 pr-10 py-2.5 border border-gray-300 rounded-xl shadow-sm placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                        value={formData.password}
                        onChange={handleChange}
                        placeholder="••••••••"
                      />
                      <button
                        type="button"
                        tabIndex={-1}
                        className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-indigo-500 focus:outline-none"
                        onClick={() => setShowPassword((prev) => !prev)}
                        aria-label={showPassword ? "Hide password" : "Show password"}
                      >
                        {showPassword ? (
                          <EyeSlashIcon className="h-5 w-5" />
                        ) : (
                          <EyeIcon className="h-5 w-5" />
                        )}
                      </button>
                    </div>
                  </div>

                  <div className="flex justify-end">
                    <Link to="/forgot-password" className="text-sm text-indigo-600 hover:text-indigo-500 font-medium">
                      Forgot your password?
                    </Link>
                  </div>

                  <div className="text-center mt-6">
                    <button
                      type="submit"
                      disabled={loading}
                      className="w-full flex justify-center py-3 px-4 border border-transparent rounded-xl shadow-sm text-sm font-medium text-white bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 transition-all duration-200"
                    >
                      {loading ? (
                        <div className="flex items-center justify-center">
                          <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                            <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                            <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                          </svg>
                          Signing in...
                        </div>
                      ) : (
                        'Sign in'
                      )}
                    </button>
                  </div>
                </form>

                <div className="text-center mt-6">
                  <p className="text-sm text-gray-600">
                    Don't have an account?{' '}
                    <Link to="/register" className="font-medium text-indigo-600 hover:text-indigo-500">
                      Create one
                    </Link>
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default Login;
